import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SEO } from "@/components/SEO/SEO";
import { useCompanyFlow } from "@/hooks/useCompanyFlow";
import { Skeleton } from "antd";
import * as _ from "lodash";
import toast from "react-hot-toast";

import { formatMoney } from "@/lib";
import { resolverPrecioProducto, resolverBadgesDescuento } from "@/lib/precioProducto";

import { ListaProductosInfinita } from "@/hooks/useProductos";
import { useMaquinaById, DestacadosByMaquina } from "@/hooks/useMaquinas";
import { useMachine } from "@/_zustand/machine";
import { useAuth } from "@/_zustand/auth";

// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/grid";
import "swiper/css/pagination";

import "./components/styles.css";
import "./components/storedetail.css";

// import required modules
import { Grid, Pagination } from "swiper/modules";

import { useProductStore } from "@/_zustand/store";
import { RedTag } from "./components/RedTag";
import { BlueTag } from "./components/BlueTag";
import { OfferTag } from "./components/OfferTag";
import { ModalProductDetail } from "./components/ModalProductDetail";
import { BannersByEmpresa } from "@/hooks/useEmpresas";
import { Distance } from "./components/Distance";
import { ModalStoreCart } from "./components/ModalStoreCart";
import { ModalVendeLicor } from "./components/ModalVendeLicor";

export const StorePage = () => {
  const navigate = useNavigate();
  const { companyHomePath } = useCompanyFlow();
  const {
    addressMachine,
    machine,
    nameEmpresa,
    logoMachine,
    setMachine,
    empresa_id,
    machineSelected,
  } = useMachine();
  const { addToCart, calculateTotals, products, removeFromCart, setDiscount, setStoreToPay } =
    useProductStore();
  const { userAddress } = useAuth();
  const latitud = userAddress?.length === 2 ? Number(userAddress[0]) : -33.4369;
  const longitud =
    userAddress?.length === 2 ? Number(userAddress[1]) : -70.6693;
  let { empresaID } = useParams();
  const {
    data: infiniteData,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = ListaProductosInfinita(empresaID);

  const dataProductosDisponibles = useMemo(
    () =>
      infiniteData?.pages.flatMap((page) =>
        (page.data ?? []).map((p) => ({ ...p, stock: Number(p.stock) })),
      ) ?? [],
    [infiniteData],
  );

  const [searchItem, setSearchItem] = useState("");
  const getMaquinaById = useMaquinaById();
  const [show, setShow] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showModalStoreCart, setShowModalStoreCart] = useState(false);

  // Única fuente de verdad para "cantidad en carrito" en esta página: se deriva
  // directamente de `products` (store real de Zustand), nunca de un espejo local.
  const cartQuantities = useMemo(() => {
    const map = {};
    products.forEach((item) => {
      if (item.maquina_id === machineSelected && Number(item.producto_cant) > 0) {
        map[item.producto_id] = Number(item.producto_cant);
      }
    });
    return map;
  }, [products, machineSelected]);

  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const [, setLogoUrl] = useState(
    "@/assets/imgs/products/product_placeholder.jpg",
  );
  const banners = BannersByEmpresa(empresa_id);

  useEffect(() => {
    if (_.isEmpty(machine) || _.size(machine) == 0) {
      getMaquinaById.mutateAsync(empresaID).then((resp) => {
        if (_.size(resp) == 0) {
          //navigate al home porque la maquina no se encontro
          toast.error("Hay un error con esta máquina.");
          navigate(companyHomePath, { replace: false });
          return;
        }

        if (resp[0].estado_id != 1) {
          //navigate al home no esta activa
          toast.error("Esta máquina no se encuentra activa.");
          navigate(companyHomePath, { replace: false });
          return;
        }

        let maquinaResp = resp[0];

        setLogoUrl(maquinaResp.maquinamodelo_icono);
        const maquinaMap = {
          comuna_nombre: maquinaResp.comuna_nombre,
          region_nombre: maquinaResp.region_nombre,
          empresa_id: maquinaResp.empresa_id,
          empresa_nombre: maquinaResp.empresa_nombre,
          empresa_rut: "",
          maquina_direccion: maquinaResp.maquina_direccion,
          maquina_latitud: maquinaResp.maquina_latitud,
          maquina_longitud: maquinaResp.maquina_longitud,
          maquina_nombre: maquinaResp.maquina_nombre,
          maquinamodelo_icono: maquinaResp.maquinamodelo_icono,
          oneclick_apikey: maquinaResp.oneclick_apikey,
          oneclick_codigo_comercio: maquinaResp.oneclick_codigo_comercio,
          oneclick_codigo_tienda: maquinaResp.oneclick_codigo_tienda,
          webpay_apikey: maquinaResp.webpay_apikey,
          webpay_codigo_comercio: maquinaResp.webpay_codigo_comercio,
          maquina_id: maquinaResp.maquina_id,
          vende_licor: maquinaResp.vende_licor,
          maquina_telefono: maquinaResp.maquina_telefono,
        };

        if (maquinaMap.vende_licor == 1) {
          setShow(true);
        }
        setMachine(maquinaMap);
      });
    }
    // Solo debe re-ejecutarse cuando cambia la máquina seleccionada: `machine` se
    // usa únicamente como guarda (isEmpty) dentro del efecto, y companyHomePath/
    // empresaID/getMaquinaById/navigate/setMachine son estables o no deben
    // disparar un nuevo fetch por sí mismos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineSelected]);

  const { data: destacados } = DestacadosByMaquina(machineSelected);

  const handleAddToCart = (product) => {
    if (!machineSelected) return;

    const currentQuantity = cartQuantities[product.producto_id] ?? 0;
    const limiteStock = Math.min(
      product.stock ?? Infinity,
      product.stock_maximo ?? Infinity,
    );
    if (currentQuantity + 1 > limiteStock) {
      toast.error(
        `No puedes agregar más de ${limiteStock} unidades de este producto.`,
      );
      return;
    }

    const dataToAdd = {
      ...product,
      producto_cant: currentQuantity + 1,
      maquina_id: machineSelected,
      logo_machine: logoMachine,
      name_empresa: nameEmpresa,
      address_machine: addressMachine,
      empresa_id: empresa_id,
    };
    addToCart(dataToAdd, machineSelected);
    setStoreToPay(machineSelected);
    calculateTotals(machineSelected);

    toast.success("Producto agregado al carrito");
    setSelectedProduct(dataToAdd);
    setShowModalStoreCart(true);
  };

  const ProductCard = ({
    product,
    isInCart,
    quantity,
    onAddToCart,
    onQuantityChange,
  }) => {
    const { precioFinal, precioOriginal, tieneDescuento } =
      resolverPrecioProducto(product);
    const badges = resolverBadgesDescuento(product);
    return (
      <div className="product-card" key={product.producto_id}>
        <div className="relative overflow-hidden w-full bg-gray-100 rounded-t-lg">
          {isInCart ? (
            // Selector de cantidad si el producto está en el carrito
            <div className="quantity-selector-container-pc">
              <div className="quantity-selector">
                <button
                  className="quantity-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuantityChange(-1);
                  }}
                >
                  –
                </button>
                <span className="quantity-display">{quantity}</span>
                <button
                  className="quantity-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuantityChange(1);
                  }}
                >
                  +
                </button>
              </div>
            </div>
          ) : (
            // Botón de añadir (+) si el producto NO está en el carrito
            <button
              className="add-to-cart-button-design-pc"
              onClick={(e) => {
                e.stopPropagation();
                onAddToCart();
              }}
            >
              +
            </button>
          )}

          <img
            src={product.producto_foto}
            alt={product.producto_nombre}
            className="w-full h-auto object-cover" // Clases de imagen
          />
        </div>

        <div className="product-info">
          <h4 className="product-name">{product.producto_nombre}</h4>
          {/* Sin badges no se renderiza el contenedor: el ladder anterior
              siempre cerraba con un <GreenTag/> "¡Nuevo!" en los productos
              sin ningún descuento, así que `.tags` nunca quedaba vacío. Al
              unificar los badges ese fallback desapareció (etiquetar de
              "¡Nuevo!" a todo producto sin descuento era incorrecto), y un
              `.tags` vacío deja un hueco de layout. */}
          {badges.length > 0 && (
            <div className="tags">
              {badges.map((badge, i) =>
                badge.tipo === "porcentaje" ||
                badge.tipo === "porcentaje-oferta" ? (
                  <RedTag key={i} percentage={badge.percentage} />
                ) : badge.tipo === "2x1" ? (
                  <BlueTag key={i} />
                ) : (
                  <OfferTag key={i} />
                ),
              )}
            </div>
          )}

          <div className="product-price">
            {tieneDescuento && (
              <span className="original-price">
                {formatMoney(precioOriginal)}
              </span>
            )}
            <span className="text-black montserrat-ultrabold">
              {formatMoney(precioFinal)}
            </span>
          </div>
        </div>
        {/* <i className="add-icon" /> */}
      </div>
    );
  };

  const handleClickProduct = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const handleQuantityChange = (product, change) => {
    if (!machineSelected) return;

    const currentQuantity = cartQuantities[product.producto_id] ?? 0;
    const newQuantity = currentQuantity + change;

    // Idempotencia: evita que un doble clic en "-" intente restar de algo que
    // ya no está en el carrito.
    if (change < 0 && currentQuantity <= 0) return;

    // --- Lógica de validación del stock ---
    const limiteStock = Math.min(
      product.stock ?? Infinity,
      product.stock_maximo ?? Infinity,
    );
    if (change > 0 && newQuantity > limiteStock) {
      toast.error(
        `No puedes agregar más de ${limiteStock} unidades de este producto.`,
      );
      return;
    }

    if (newQuantity <= 0) {
      // Mismo camino que ya usa ProductsComponent.handleRemoveProduct (Carrito):
      // elimina del store real, recalcula totales y limpia el cupón aplicado.
      removeFromCart(product.producto_id, machineSelected);
      calculateTotals(machineSelected);
      setDiscount(0, 0, null);

      const remainingForMachine = products.filter(
        (item) =>
          item.maquina_id === machineSelected &&
          item.producto_id !== product.producto_id &&
          Number(item.producto_cant) > 0,
      );
      if (remainingForMachine.length === 0 && showModalStoreCart) {
        setShowModalStoreCart(false);
      }
      return;
    }

    // Actualiza solo la cantidad — NO reemplazar con datos del API (puede perder descuentos).
    // Alineado con CartContext (Sima-appmovil): addToCart para existentes solo toca producto_cant.
    let dataToAdd = {
      ...product,
      producto_cant: newQuantity,
      maquina_id: machineSelected,
      logo_machine: logoMachine,
      address_machine: addressMachine,
      name_empresa: nameEmpresa,
    };
    addToCart(dataToAdd, machineSelected);
    calculateTotals(machineSelected);
    setShowModalStoreCart(true);
  };

  const AvailableCard = ({
    product,
    isInCart,
    quantity,
    onAddToCart,
    onQuantityChange,
  }) => {
    const { precioFinal, precioOriginal, tieneDescuento } =
      resolverPrecioProducto(product);
    const badges = resolverBadgesDescuento(product);
    return (
      <div
        className="product-card-design"
        key={product.producto_id}
        onClick={() => handleClickProduct(product)}
      >
        {/* Contenido de la izquierda (información del producto) */}
        <div className="product-info-left">
          <h4 className="product-name-design">{product.producto_nombre}</h4>

          {/* Precio actual + precio original tachado + etiquetas */}
          <div className="price-and-tags-container">
            <div>
              <span className="current-price">
                {formatMoney(precioFinal)} CLP
              </span>
              {tieneDescuento && (
                <span className="original-price-design">
                  {formatMoney(precioOriginal)} CLP
                </span>
              )}
            </div>
            <div className="tags">
              {badges.map((badge, i) =>
                badge.tipo === "porcentaje" ||
                badge.tipo === "porcentaje-oferta" ? (
                  <RedTag key={i} percentage={badge.percentage} />
                ) : badge.tipo === "2x1" ? (
                  <BlueTag key={i} />
                ) : (
                  <OfferTag key={i} />
                ),
              )}
            </div>
          </div>

          {/* <p className="product-description-design">
            {product.producto_descripcion}
          </p> */}
        </div>

        {/* Contenedor de la imagen del producto con el botón de añadir */}
        <div className="product-image-container-design">
          <img
            src={product.producto_foto}
            alt={product.producto_nombre}
            className="product-image-design"
          />
          {/* Botón de añadir (el signo +) */}
          {isInCart ? (
            // Selector de cantidad si el producto está en el carrito
            <div className="quantity-selector-container">
              <div className="quantity-selector">
                <button
                  className="quantity-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuantityChange(-1);
                  }}
                >
                  –
                </button>
                <span className="quantity-display">{quantity}</span>
                <button
                  className="quantity-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuantityChange(1);
                  }}
                >
                  +
                </button>
              </div>
            </div>
          ) : (
            // Botón de añadir (+) si el producto NO está en el carrito
            <button
              className="add-to-cart-button-design"
              onClick={(e) => {
                e.stopPropagation();
                onAddToCart();
              }}
            >
              +
            </button>
          )}
        </div>
      </div>
    );
  };

  const AvailableProducts = (products) => {
    return (
      <div className="available-products">
        <h2 className="available-title">Productos disponibles</h2>

        <div className="available-grid">
          <SwiperStorageAvailable products={products.products} />
        </div>
      </div>
    );
  };

  const SwiperStorage = () => {
    if (_.size(destacados) > 0) {
      if (_.size(destacados) > 3) {
        return (
          <Swiper
            slidesPerView={4}
            grid={{
              rows: 3,
              fill: "row",
            }}
            spaceBetween={10}
            pagination={{
              clickable: true,
            }}
            modules={[Grid, Pagination]}
            className="mySwiper"
            breakpoints={{
              340: {
                slidesPerView: 4,
                spaceBetween: 20,
              },
              768: {
                slidesPerView: 5,
                spaceBetween: 10,
              },
              1024: {
                slidesPerView: 6,
                spaceBetween: 5,
              },
            }}
          >
            {_.size(destacados) > 0 ? (
              destacados.map((product) => {
                const quantityInCart = cartQuantities[product.producto_id] || 0;
                const isInCart = quantityInCart > 0;
                return (
                  <SwiperSlide key={product.producto_id}>
                    <ProductCard
                      product={product}
                      isInCart={isInCart}
                      quantity={quantityInCart}
                      onAddToCart={() => handleAddToCart(product)}
                      onQuantityChange={(change) =>
                        handleQuantityChange(product, change)
                      }
                    />
                  </SwiperSlide>
                );
              })
            ) : (
              <Skeleton avatar paragraph={{ rows: 4 }} />
            )}
          </Swiper>
        );
      } else {
        return (
          <Swiper
            slidesPerView={6}
            grid={{
              rows: 1,
              fill: "row",
            }}
            spaceBetween={5}
            pagination={{
              clickable: true,
            }}
            modules={[Grid, Pagination]}
            className="mySwiper"
            breakpoints={{
              340: {
                slidesPerView: 3,
                spaceBetween: 10,
              },
              768: {
                slidesPerView: 5,
                spaceBetween: 10,
              },
              1024: {
                slidesPerView: 6,
                spaceBetween: 5,
              },
            }}
          >
            {_.size(destacados) > 0 ? (
              destacados.map((product) => {
                const quantityInCart = cartQuantities[product.producto_id] || 0;
                const isInCart = quantityInCart > 0;
                return (
                  <SwiperSlide key={product.producto_id}>
                    <ProductCard
                      product={product}
                      isInCart={isInCart}
                      quantity={quantityInCart}
                      onAddToCart={() => handleAddToCart(product)}
                      onQuantityChange={(change) =>
                        handleQuantityChange(product, change)
                      }
                    />
                  </SwiperSlide>
                );
              })
            ) : (
              <Skeleton avatar paragraph={{ rows: 4 }} />
            )}
          </Swiper>
        );
      }
    }
  };

  const SwiperStorageAvailable = (products) => {
    if (_.size(products) > 0) {
      if (_.size(products.products) > 3) {
        return (
          <Swiper
            slidesPerView={3}
            grid={{
              rows: 3,
              fill: "row",
            }}
            spaceBetween={10}
            pagination={{
              clickable: true,
            }}
            modules={[Grid, Pagination]}
            className="mySwiper"
            breakpoints={{
              340: {
                slidesPerView: 1,
                spaceBetween: 20,
              },
              768: {
                slidesPerView: 3,
                spaceBetween: 10,
              },
              1024: {
                slidesPerView: 4,
                spaceBetween: 5,
              },
            }}
          >
            {_.size(products.products) > 0 ? (
              products.products.map((product) => {
                const quantityInCart = cartQuantities[product.producto_id] || 0;
                const isInCart = quantityInCart > 0;

                return (
                  <SwiperSlide key={product.producto_id}>
                    <AvailableCard
                      product={product}
                      isInCart={isInCart}
                      quantity={quantityInCart}
                      onAddToCart={() => handleAddToCart(product)}
                      onQuantityChange={(change) =>
                        handleQuantityChange(product, change)
                      }
                    />
                  </SwiperSlide>
                );
              })
            ) : (
              <Skeleton avatar paragraph={{ rows: 4 }} />
            )}
          </Swiper>
        );
      } else {
        return (
          <Swiper
            slidesPerView={3}
            grid={{
              rows: 1,
              fill: "row",
            }}
            spaceBetween={5}
            pagination={{
              clickable: true,
            }}
            modules={[Grid, Pagination]}
            className="mySwiper"
            breakpoints={{
              340: {
                slidesPerView: 1,
                spaceBetween: 10,
              },
              768: {
                slidesPerView: 2,
                spaceBetween: 10,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 5,
              },
            }}
          >
            {_.size(products.products) > 0 ? (
              products.products.map((product) => {
                const quantityInCart = cartQuantities[product.producto_id] || 0;
                const isInCart = quantityInCart > 0;
                return (
                  <SwiperSlide key={product.producto_id}>
                    <AvailableCard
                      product={product}
                      isInCart={isInCart}
                      quantity={quantityInCart}
                      onAddToCart={() => handleAddToCart(product)}
                      onQuantityChange={(change) =>
                        handleQuantityChange(product, change)
                      }
                    />
                  </SwiperSlide>
                );
              })
            ) : (
              <Skeleton avatar paragraph={{ rows: 4 }} />
            )}
          </Swiper>
        );
      }
    } else {
      return (
        <h4 className="text-center prompt-bold text-black mb-3">
          No se encontraron productos disponibles.
        </h4>
      );
    }
  };

  const handleInputChange = (e) => {
    setSearchItem(e.target.value);
  };

  const filteredProducts = dataProductosDisponibles
    ? _.uniqBy(
        dataProductosDisponibles.filter((item) =>
          item.producto_nombre.toLowerCase().includes(searchItem.toLowerCase()),
        ),
        "producto_id",
      )
    : [];

  const HeaderImage = ({ imageUrl }) => {
    return (
      <div className="header-image">
        {banners.data ? (
          <img src={banners.data.imagen} />
        ) : (
          <img src={imageUrl} />
        )}
      </div>
    );
  };

  const BrandInfo = ({ logoUrl, name, address }) => {
    if (machine)
      return (
        <div className="brand-info">
          <div className="brand-details">
            <img src={logoUrl} alt={`${name} logo`} className="brand-logo" />
            <div style={{ minWidth: "300px" }}>
              <h2 className="brand-name">{name}</h2>
              <div className="brand-location">
                <i className="icon-location" />
                <span>
                  <Distance
                    lat1={machine.maquina_latitud}
                    lon1={machine.maquina_longitud}
                    lat2={latitud}
                    lon2={longitud}
                    address={address}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      );
  };

  const FeaturedProducts = ({ title, onViewAll }) => {
    return (
      <div className="featured-products">
        <div className="featured-header">
          <h2>{title}</h2>
          {destacados && destacados.length > 6 ? (
            <button className="view-all-btn" onClick={onViewAll}>
              Ver todos
            </button>
          ) : null}
        </div>

        <div className="product-carousel">
          <SwiperStorage />
        </div>
      </div>
    );
  };

  // 1. Si está cargando, muestra solo el esqueleto.
  if (isLoading) {
    return (
      <div className="available-products">
        <h2 className="available-title">Productos disponibles loading</h2>
        <div className="available-grid">
          <Skeleton avatar paragraph={{ rows: 4 }} />
        </div>
      </div>
    );
  }

  // 2. Si hay un error, muestra un mensaje.
  if (isError) {
    return <div>Error al cargar los productos: {error.message}</div>;
  }

  // 3. Si todo está bien, muestra la lista de productos.
  return (
    <div id="todosProductos" className="container my-5" style={{ flexGrow: 1 }}>
      <SEO
        title="Tienda"
        description="Explora los productos disponibles en esta tienda vending."
        url="/store"
      />
      <ModalVendeLicor show={show} setShow={setShow} />
      {showModal && selectedProduct && (
        <ModalProductDetail
          product={selectedProduct}
          show={showModal}
          handleClose={() => setShowModal(false)}
        />
      )}
      <ModalStoreCart
        isOpen={showModalStoreCart}
        product={selectedProduct}
        onClose={() => setShowModalStoreCart(false)}
      />
      <HeaderImage />
      <div className="brand-header">
        <BrandInfo
          logoUrl={logoMachine}
          name={nameEmpresa}
          address={addressMachine}
          distance={"13km"}
        />

        {/* <Buscador onChange={handleInputChange} /> */}
        <div className="search-bar-store">
          <i className="fa fa-search"></i>
          <input
            type="text"
            value={searchItem}
            onChange={handleInputChange}
            className="search-input-bar"
            placeholder="Buscador"
          />
        </div>
      </div>
      <div className="row mb-3">
        {_.size(destacados) > 0 && <FeaturedProducts title={"Destacados"} />}
        {_.size(filteredProducts) > 0 ? (
          <AvailableProducts products={filteredProducts} />
        ) : (
          <h4 className="text-center prompt-bold text-black mb-3">
            No se encontraron productos disponibles.
          </h4>
        )}
        <div ref={sentinelRef} style={{ height: "1px" }} />
        {isFetchingNextPage && (
          <div className="text-center my-3">
            <Skeleton paragraph={{ rows: 2 }} />
          </div>
        )}
      </div>
    </div>
  );
};
